import { useState, useRef, useEffect, useContext } from "react";
import { capitalizeFirstLetter } from "../../../controller/global.function.library";

import { useTasks } from "../../../model/contexts/task.context";
import { ProjectContext } from "../../../model/contexts/project.context";
import { UserContext } from "../../../model/contexts/user.context";
import { NoteContext } from "../../../model/contexts/note.context";
import { TemplateContext } from "../../../model/contexts/template.context";

import TaskModalForm from "../TaskModalForm/TaskModalForm";
import TaskListFeed from "../TaskListFeed/TaskListFeed";
import NoteModalForm from "../NoteModalForm/NoteModalForm";

import "./TaskDashboardContent.scss";

const createTaskPayloadFromTemplateTask = ({
  template,
  templateTask,
  selectedProjectId,
}) => {
  return {
    name: templateTask.title || "",
    description: templateTask.description || "",
    priority: "1",
    status: "Not Started",
    dateCreated: "",
    dateModified: "",
    projectID: selectedProjectId,
    tag: template.tag || "",
  };
};

const TaskDashboardContent = () => {
  const dropdownRef = useRef(null);
  const templateDropdownRef = useRef(null);

  const { currentProject } = useContext(ProjectContext);

  const { templates, templateLoading, templateError } =
    useContext(TemplateContext);

  const {
    user,
    currentUser,
    authUser: contextAuthUser,
  } = useContext(UserContext);

  const authUser = contextAuthUser || user || currentUser;

  const {
    tasks,
    setCurrentTask,
    loadTasksByProject,
    addTask,
    editTask,
    editTaskTag,
  } = useTasks();

  const { setNotes } = useContext(NoteContext);

  const [isTaskFormModalVisible, setIsTaskFormModalVisible] = useState(false);
  const [isFormEditMode, setIsFormEditMode] = useState(true);
  const [isNoteFormModalVisible, setIsNoteFormModalVisible] = useState(false);

  const [isTemplatePickerVisible, setIsTemplatePickerVisible] = useState(false);
  const [templateLoadErrorMessage, setTemplateLoadErrorMessage] = useState("");
  const [isLoadingTemplateTasks, setIsLoadingTemplateTasks] = useState(false);

  const [taskErrorMessage, setTaskErrorMessage] = useState("");
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [sortOption, setSortOption] = useState("Sort By");
  const [taskTags, setTaskTags] = useState([]);
  const [selectedMenuTag, setSelectedMenuTag] = useState("All");
  const [filteredTasks, setFilteredTasks] = useState([]);

  const [taskData, setTaskData] = useState({
    name: "",
    description: "",
    priority: "1",
    status: "Not Started",
    dateCreated: "",
    dateModified: "",
    projectID: "",
    tag: "",
  });

  const [note, setNote] = useState({
    description: "",
  });

  const [noteErrorMessage, setNoteErrorMessage] = useState("");

  const selectedProjectId =
    currentProject?.id || localStorage.getItem("selectProjectID");

  const selectedProjectName =
    currentProject?.name || localStorage.getItem("selectProjectName");

  const selectedProjectIsEditable =
    currentProject?.isEditable === true ||
    currentProject?.accessMode === "edit" ||
    localStorage.getItem("selectedProjectIsEditable") === "true";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }

      if (
        templateDropdownRef.current &&
        !templateDropdownRef.current.contains(event.target)
      ) {
        setIsTemplatePickerVisible(false);
        setTemplateLoadErrorMessage("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!authUser?.uid || !selectedProjectId) return;

    const fetchTasks = async () => {
      try {
        await loadTasksByProject({
          uid: authUser.uid,
          projectId: selectedProjectId,
        });
      } catch (error) {
        console.error(
          "TaskDashboardContent loadTasksByProject error:",
          error?.response?.data || error.message,
        );
      }
    };

    fetchTasks();
  }, [authUser?.uid, selectedProjectId]);

  useEffect(() => {
    createTaskTags();
    setFilteredTasks(tasks.filter((task) => task.tag !== "archived") || []);
    setSelectedTag(selectedMenuTag);
  }, [tasks]);

  const createTaskTags = () => {
    // const uniqueTaskTags = new Set();
    // const tagList = ["All"];

    // for (const userTask of tasks) {
    //   const tag = capitalizeFirstLetter(userTask.tag || "");

    //   if (tag && tag.toLowerCase() !== "archived" && !uniqueTaskTags.has(tag)) {
    //     uniqueTaskTags.add(tag);
    //     tagList.push(tag);
    //   }
    // }

    // setTaskTags(tagList);
    // return tagList;
    const uniqueTaskTags = new Set();
    const tagList = ["All", "Archived"];

    for (const userTask of tasks) {
      const tag = capitalizeFirstLetter(userTask.tag || "");

      if (tag && !uniqueTaskTags.has(tag) && tag !== "Archived") {
        uniqueTaskTags.add(tag);
        tagList.push(tag);
      }
    }

    setTaskTags(tagList);
    return tagList;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setTaskData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleTaskFormEntry = async (event) => {
    if (event) {
      event.preventDefault();
    }

    if (!authUser?.uid || !selectedProjectId) {
      setTaskErrorMessage("No selected project found.");
      return;
    }

    if (!taskData.name.trim()) {
      setTaskErrorMessage("Task Name cannot be empty!");
      return;
    }

    try {
      setTaskErrorMessage("");

      if (taskData.id) {
        await editTask({
          uid: authUser.uid,
          projectId: selectedProjectId,
          taskId: taskData.id,
          updates: {
            ...taskData,
            projectID: selectedProjectId,
          },
        });
      } else {
        await addTask({
          uid: authUser.uid,
          projectId: selectedProjectId,
          task: {
            ...taskData,
            projectID: selectedProjectId,
          },
        });
      }

      resetTaskForm();
      setCurrentTask(null);
      closeTaskFormModal();
    } catch (error) {
      console.error(
        "handleTaskFormEntry error:",
        error?.response?.data || error.message,
      );

      setTaskErrorMessage(
        error?.response?.data?.error || error.message || "Failed to save task.",
      );
    }
  };

  const resetTaskForm = () => {
    setTaskData({
      name: "",
      description: "",
      priority: "1",
      status: "Not Started",
      dateCreated: "",
      dateModified: "",
      projectID: selectedProjectId || "",
      tag: "",
    });

    setTaskErrorMessage("");
  };

  const closeTaskFormModal = () => {
    setIsTaskFormModalVisible(false);
    resetTaskForm();
    setCurrentTask(null);
  };

  const handleSortOptionSelect = (option) => {
    setSortOption(option);
    setSortDropdownOpen(false);

    switch (option) {
      case "Sorted: By Ascending Name":
        sortTasksByAscendingName();
        break;
      case "Sorted: By Descending Name":
        sortTasksByDescendingName();
        break;
      case "Sorted: Date Created (Oldest)":
        sortTasksByDateCreatedOldest();
        break;
      case "Sorted: Date Created (Newest)":
        sortTasksByDateCreatedNewest();
        break;
      case "Sorted: Date Modified (Oldest)":
        sortTasksByDateModifiedOldest();
        break;
      case "Sorted: Date Modified (Newest)":
        sortTasksByDateModifiedNewest();
        break;
      case "Sorted: Priority":
        sortTasksByPriority();
        break;
      case "Sorted: Progress":
        sortTasksByProgress();
        break;
      default:
        setSortOption("Sort By");
        console.warn("Unknown sort option selected");
    }
  };

  const sortTasksByAscendingName = () => {
    const sortedTasks = [...filteredTasks].sort((currentTask, nextTask) =>
      currentTask.name.toLowerCase().localeCompare(nextTask.name.toLowerCase()),
    );

    setFilteredTasks(sortedTasks);
  };

  const sortTasksByDescendingName = () => {
    const sortedTasks = [...filteredTasks].sort((currentTask, nextTask) =>
      nextTask.name.toLowerCase().localeCompare(currentTask.name.toLowerCase()),
    );

    setFilteredTasks(sortedTasks);
  };

  const sortTasksByDateCreatedOldest = () => {
    const sortedTasks = [...filteredTasks].sort((currentTask, nextTask) =>
      (currentTask.dateCreated || "").localeCompare(nextTask.dateCreated || ""),
    );

    setFilteredTasks(sortedTasks);
  };

  const sortTasksByDateCreatedNewest = () => {
    const sortedTasks = [...filteredTasks].sort((currentTask, nextTask) =>
      (nextTask.dateCreated || "").localeCompare(currentTask.dateCreated || ""),
    );

    setFilteredTasks(sortedTasks);
  };

  const sortTasksByDateModifiedOldest = () => {
    const sortedTasks = [...filteredTasks].sort((currentTask, nextTask) =>
      (currentTask.dateModified || "").localeCompare(
        nextTask.dateModified || "",
      ),
    );

    setFilteredTasks(sortedTasks);
  };

  const sortTasksByDateModifiedNewest = () => {
    const sortedTasks = [...filteredTasks].sort((currentTask, nextTask) =>
      (nextTask.dateModified || "").localeCompare(
        currentTask.dateModified || "",
      ),
    );

    setFilteredTasks(sortedTasks);
  };

  const sortTasksByPriority = () => {
    const sortedTasks = [...filteredTasks].sort((currentTask, nextTask) => {
      const currentPriority = parseInt(currentTask.priority);
      const nextPriority = parseInt(nextTask.priority);

      if (currentPriority === nextPriority) {
        return (currentTask.dateCreated || "").localeCompare(
          nextTask.dateCreated || "",
        );
      }

      return nextPriority - currentPriority;
    });

    setFilteredTasks(sortedTasks);
  };

  const sortTasksByProgress = () => {
    const statusOrder = {
      Completed: 0,
      "On Hold": 1,
      "In Progress": 2,
      "Not Started": 3,
    };

    const sortedTasks = [...filteredTasks].sort((currentTask, nextTask) => {
      const currentStatus = statusOrder[currentTask.status] ?? Infinity;
      const nextStatus = statusOrder[nextTask.status] ?? Infinity;

      if (currentStatus === nextStatus) {
        return (currentTask.dateCreated || "").localeCompare(
          nextTask.dateCreated || "",
        );
      }

      return currentStatus - nextStatus;
    });

    setFilteredTasks(sortedTasks);
  };

  const setSelectedTag = (tag) => {
    setSortOption("Sort By");
    setSelectedMenuTag(tag);

    if (tag === "All") {
      setFilteredTasks(tasks.filter((task) => task.tag !== "archived") || []);
      return;
    }

    if (tag === "Untagged") {
      setFilteredTasks(
        tasks.filter((task) => (task.tag || "").toLowerCase() === ""),
      );
      return;
    }

    setFilteredTasks(
      tasks.filter(
        (task) => (task.tag || "").toLowerCase() === tag.toLowerCase(),
      ),
    );
  };

  const setArchiveTag = async (projectId, task, tag) => {
    if (!authUser?.uid) return;

    await editTaskTag({
      uid: authUser.uid,
      projectId,
      taskId: task.id,
      tag,
    });
  };

  const undoArchiveTag = async (projectId, task, tag) => {
    if (!authUser?.uid) return;

    setSelectedMenuTag("All");

    await editTaskTag({
      uid: authUser.uid,
      projectId,
      taskId: task.id,
      tag,
    });
  };

  const resetNoteForm = () => {
    setNote({
      description: "",
    });

    setNoteErrorMessage("");
  };

  const closeNoteFormModal = () => {
    resetNoteForm();
    localStorage.removeItem("selectTaskID");
    localStorage.removeItem("selectTaskName");
    setNotes([]);
    setIsNoteFormModalVisible(false);
  };

  const dateConverted = (dateInput) => {
    const date = new Date(dateInput);

    return date.toLocaleString([], {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleNewTaskClick = () => {
    if (!selectedProjectIsEditable) {
      return;
    }

    resetTaskForm();
    setCurrentTask(null);
    setIsFormEditMode(true);
    setIsTaskFormModalVisible(true);
  };

  const handleLoadTemplateClick = () => {
    if (!selectedProjectIsEditable) {
      return;
    }

    setTemplateLoadErrorMessage("");
    setIsTemplatePickerVisible((currentValue) => !currentValue);
  };

  const closeTemplatePicker = () => {
    setTemplateLoadErrorMessage("");
    setIsTemplatePickerVisible(false);
  };

  const handleLoadTemplateIntoTaskList = async (template) => {
    try {
      if (!authUser?.uid || !selectedProjectId) {
        setTemplateLoadErrorMessage("No selected project found.");
        return;
      }

      if (!template?.tasks || template.tasks.length === 0) {
        setTemplateLoadErrorMessage("This template does not have any tasks.");
        return;
      }

      const validTemplateTasks = template.tasks.filter((templateTask) =>
        templateTask.title?.trim(),
      );

      if (validTemplateTasks.length === 0) {
        setTemplateLoadErrorMessage(
          "This template does not have any task titles.",
        );
        return;
      }

      setIsLoadingTemplateTasks(true);
      setTemplateLoadErrorMessage("");

      for (const templateTask of validTemplateTasks) {
        const taskPayload = createTaskPayloadFromTemplateTask({
          template,
          templateTask,
          selectedProjectId,
        });

        await addTask({
          uid: authUser.uid,
          projectId: selectedProjectId,
          task: taskPayload,
        });
      }

      setSelectedTag("All");
      closeTemplatePicker();
    } catch (error) {
      console.error("handleLoadTemplateIntoTaskList error:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error.message,
      });

      setTemplateLoadErrorMessage(
        error?.response?.data?.error ||
          error.message ||
          "Failed to load template.",
      );
    } finally {
      setIsLoadingTemplateTasks(false);
    }
  };

  if (!selectedProjectId) {
    return (
      <div className="task-dashboard-no-project-message">
        No project selected.
      </div>
    );
  }

  return (
    <div className="task-dashboard-content-container">
      <div className="task-dashboard-header">
        <h2>{selectedProjectName || "Project"}</h2>
        <h3>Task List</h3>
      </div>

      <div className="task-dashboard-menu">
        <div className="task-dashboard-left-side">
          <div className="task-dashboard-filter-label-container">
            <div className="task-dashboard-filter-label">Filter By Label:</div>

            <div className="task-dashboard-tag-strip">
              {taskTags.map((tag) => (
                <div
                  key={tag}
                  className={`task-dashboard-tag-chip ${
                    selectedMenuTag === tag ? "active" : ""
                  }`}
                  onClick={() => setSelectedTag(tag)}
                >
                  {tag}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="task-dashboard-right-side">
          <div
            className="task-dashboard-template-dropdown"
            ref={templateDropdownRef}
          >
            <button
              type="button"
              className="task-dashboard-menu-action-button task-dashboard-load-template-button"
              onClick={handleLoadTemplateClick}
              disabled={!selectedProjectIsEditable}
            >
              Load Template
            </button>

            {isTemplatePickerVisible && (
              <div className="task-dashboard-template-dropdown-menu">
                <div className="task-dashboard-template-dropdown-header">
                  <h4>Load Template</h4>
                  <p>Add saved template tasks to this project.</p>
                </div>

                {templateError && (
                  <div className="task-dashboard-template-dropdown-error">
                    {templateError}
                  </div>
                )}

                {templateLoadErrorMessage && (
                  <div className="task-dashboard-template-dropdown-error">
                    {templateLoadErrorMessage}
                  </div>
                )}

                {templateLoading && (
                  <div className="task-dashboard-template-dropdown-state">
                    Loading templates...
                  </div>
                )}

                {!templateLoading && templates.length === 0 && (
                  <div className="task-dashboard-template-dropdown-state">
                    No templates found.
                  </div>
                )}

                {!templateLoading && templates.length > 0 && (
                  <div className="task-dashboard-template-dropdown-list">
                    {templates.map((template) => {
                      const templateId = template.templateID || template.id;

                      return (
                        <div
                          className="task-dashboard-template-dropdown-card"
                          key={templateId}
                        >
                          <div className="task-dashboard-template-dropdown-card-info">
                            <h5>{template.name}</h5>

                            {template.description && (
                              <p>{template.description}</p>
                            )}

                            <div className="task-dashboard-template-dropdown-card-meta">
                              <span>{template.tasks?.length || 0} tasks</span>

                              {template.tag?.trim() && (
                                <span>
                                  {capitalizeFirstLetter(template.tag)}
                                </span>
                              )}
                            </div>
                          </div>

                          <button
                            type="button"
                            className="task-dashboard-template-dropdown-load-button"
                            onClick={() =>
                              handleLoadTemplateIntoTaskList(template)
                            }
                            disabled={isLoadingTemplateTasks}
                          >
                            {isLoadingTemplateTasks ? "..." : "Load"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="task-dashboard-sort-dropdown" ref={dropdownRef}>
            <button
              type="button"
              className="task-dashboard-menu-action-button task-dashboard-sort-button"
              onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
            >
              {sortOption}
            </button>

            {sortDropdownOpen && (
              <div className="task-dashboard-sort-dropdown-menu">
                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: By Ascending Name")
                  }
                >
                  Name (A-Z)
                </div>

                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: By Descending Name")
                  }
                >
                  Name (Z-A)
                </div>

                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Created (Oldest)")
                  }
                >
                  Date Created (Oldest)
                </div>

                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Created (Newest)")
                  }
                >
                  Date Created (Newest)
                </div>

                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Modified (Oldest)")
                  }
                >
                  Date Modified (Oldest)
                </div>

                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Modified (Newest)")
                  }
                >
                  Date Modified (Newest)
                </div>

                <div onClick={() => handleSortOptionSelect("Sorted: Priority")}>
                  Priority
                </div>

                <div onClick={() => handleSortOptionSelect("Sorted: Progress")}>
                  Progress
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            className="task-dashboard-menu-action-button task-dashboard-new-button"
            onClick={handleNewTaskClick}
            disabled={!selectedProjectIsEditable}
          >
            {selectedProjectIsEditable ? "New Task" : "View Only"}
          </button>
        </div>
      </div>

      <TaskModalForm
        isVisible={isTaskFormModalVisible}
        isFormEditMode={isFormEditMode}
        setIsFormEditMode={setIsFormEditMode}
        onClose={closeTaskFormModal}
        taskData={taskData}
        setTaskData={setTaskData}
        handleInputChange={handleInputChange}
        handleTaskFormEntry={handleTaskFormEntry}
        taskErrorMessage={taskErrorMessage}
      />

      <NoteModalForm
        isVisible={isNoteFormModalVisible}
        onClose={closeNoteFormModal}
        note={note}
        setNote={setNote}
        resetNoteForm={resetNoteForm}
        noteErrorMessage={noteErrorMessage}
        setNoteErrorMessage={setNoteErrorMessage}
        dateConverted={dateConverted}
      />

      <div className="task-dashboard-list-feed-section">
        <TaskListFeed
          currentProject={{
            id: selectedProjectId,
            name: selectedProjectName,
            isEditable: selectedProjectIsEditable,
          }}
          selectedProjectIsEditable={selectedProjectIsEditable}
          filteredTasks={filteredTasks}
          setFilteredTasks={setFilteredTasks}
          selectedMenuTag={selectedMenuTag}
          setSelectedMenuTag={setSelectedMenuTag}
          setSelectedTag={setSelectedTag}
          setArchiveTag={setArchiveTag}
          undoArchiveTag={undoArchiveTag}
          taskData={taskData}
          setTaskData={setTaskData}
          resetForm={resetTaskForm}
          closeModal={closeTaskFormModal}
          isTaskFormModalVisible={isTaskFormModalVisible}
          setIsTaskFormModalVisible={setIsTaskFormModalVisible}
          isFormEditMode={isFormEditMode}
          setIsFormEditMode={setIsFormEditMode}
          isNoteFormModalVisible={isNoteFormModalVisible}
          setIsNoteFormModalVisible={setIsNoteFormModalVisible}
          capitalizeFirstLetter={capitalizeFirstLetter}
          dateConverted={dateConverted}
        />
      </div>
    </div>
  );
};

export default TaskDashboardContent;
